import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import Sidebar9 from '../Sidebar9';
import Footer from '../../Footer';

const months = [
  { name: 'January',  route: '/Grade9/Recordings/January'  },
  { name: 'February', route: '/Grade9/Recordings/February' },
  { name: 'March',    route: '/Grade9/Recordings/March'    },
  { name: 'April',    route: '/Grade9/Recordings/April'    },
  { name: 'May',      route: '/Grade9/Recordings/May'      },
  { name: 'June',     route: '/Grade9/Recordings/June'     },
];

const CalendarIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const VideoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="M16 10l6-4v12l-6-4V10z" />
  </svg>
);
const PlayIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3l14 9-14 9V3z" /></svg>
);
const FilmIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="2" />
    <line x1="7" y1="2" x2="7" y2="22" /><line x1="17" y1="2" x2="17" y2="22" />
    <line x1="2" y1="12" x2="22" y2="12" /><line x1="2" y1="7" x2="7" y2="7" />
    <line x1="2" y1="17" x2="7" y2="17" /><line x1="17" y1="17" x2="22" y2="17" /><line x1="17" y1="7" x2="22" y2="7" />
  </svg>
);
const SpinnerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
    </path>
  </svg>
);

const Grade9Recordings = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const results = {};
        await Promise.all(
          months.map(async ({ name }) => {
            const q = query(
              collection(db, 'recordings'),
              where('month', '==', name),
              where('grade', '==', 'Grade 9')
            );
            const snap = await getDocs(q);
            results[name] = snap.size;
          })
        );
        setCounts(results);
      } catch (err) {
        console.error('Error fetching Grade 9 recording counts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCounts();
  }, []);

  const totalRecordings = Object.values(counts).reduce((a, b) => a + b, 0);
  const color = '#F59E0B';
  const shadow = 'rgba(245,158,11,0.32)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F3F4F6' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');
        * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

        .wr-card {
          background: #fff; border-radius: 18px; overflow: hidden;
          border: 1.5px solid #E5E7EB;
          transition: transform 0.26s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.26s ease, border-color 0.26s ease;
          cursor: pointer;
        }
        .wr-card:hover { transform: translateY(-8px); }

        .wr-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 7px;
          width: 100%; padding: 11px 18px; border-radius: 10px;
          font-weight: 600; font-size: 13.5px; border: none; cursor: pointer;
          transition: filter 0.2s, transform 0.15s; letter-spacing: 0.01em; color: white;
        }
        .wr-btn:hover { filter: brightness(1.12); transform: translateY(-2px); }
        .wr-btn:active { transform: translateY(0); }

        .wr-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 22px;
        }
        @media (max-width: 580px) { .wr-grid { grid-template-columns: 1fr; } }
        @media (max-width: 767px) { .wr-content { padding-top: 60px; } }
      `}</style>

      <div style={{ display: 'flex', flexGrow: 1 }}>
        <Sidebar9 activeItem="Recordings" />

        <div className="wr-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Header */}
          <header style={{ background: 'white', borderBottom: '1px solid #E5E7EB', padding: '0 28px' }}>
            <div style={{ padding: '18px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                <div style={{
                  width: 46, height: 46, borderRadius: 13,
                  background: 'linear-gradient(135deg, #FBBF24, #D97706)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(245,158,11,0.42)',
                }}>
                  <VideoIcon />
                </div>
                <div>
                  <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.2 }}>
                    Recordings
                  </h1>
                  <p style={{ fontSize: 13, color: '#9CA3AF', margin: '3px 0 0' }}>Grade 9 recordings by month</p>
                </div>
              </div>
              {!loading && totalRecordings > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#111827', borderRadius: 10, padding: '9px 16px' }}>
                  <FilmIcon size={16} />
                  <span style={{ fontSize: 13.5, color: 'white', fontWeight: 600 }}>{totalRecordings} total recordings</span>
                </div>
              )}
            </div>
          </header>

          {/* Cards */}
          <main style={{ flexGrow: 1, padding: '28px' }}>
            <div className="wr-grid">
              {months.map((month, index) => {
                const count = counts[month.name] ?? 0;
                const isHovered = hoveredIndex === index;
                return (
                  <div
                    key={index}
                    className="wr-card"
                    style={{
                      borderColor: isHovered ? color : '#E5E7EB',
                      boxShadow: isHovered ? `0 20px 52px ${shadow}` : '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {/* Coloured header */}
                    <div style={{
                      background: `linear-gradient(135deg, ${color}, #D97706)`,
                      padding: '18px 20px 16px',
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    }}>
                      <div style={{
                        width: 46, height: 46, borderRadius: 13,
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'transform 0.22s',
                        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                      }}>
                        <CalendarIcon />
                      </div>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: 12, fontWeight: 600, padding: '4px 11px', borderRadius: 20,
                        background: 'rgba(255,255,255,0.22)', color: 'white',
                      }}>
                        {loading ? <SpinnerIcon /> : <><FilmIcon size={12} />{count} {count === 1 ? 'rec' : 'recs'}</>}
                      </span>
                    </div>

                    {/* White body */}
                    <div style={{ padding: '16px 20px 20px' }}>
                      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 700, color: '#111827', margin: '0 0 5px', letterSpacing: '-0.01em' }}>
                        {month.name}
                      </h2>
                      <p style={{ fontSize: 13, color: '#9CA3AF', margin: '0 0 16px', lineHeight: 1.5 }}>
                        {loading ? 'Loading…' : `${count} class ${count === 1 ? 'recording' : 'recordings'} available`}
                      </p>
                      <button
                        className="wr-btn"
                        onClick={() => navigate(month.route)}
                        style={{ background: `linear-gradient(135deg, ${color}, #D97706)` }}
                      >
                        <PlayIcon /> View Recordings
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Grade9Recordings;
